<script>
import moment from "moment";
import periodFilter from "./period-filter.vue";
import statCards from "./stat-cards.vue";
import proofsChart from "./proofs-chart.vue";
import activityFeed from "./activity-feed.vue";
import recentProofLists from "./recent-proof-lists.vue";
import gettingStarted from "./getting-started.vue";
import jobsDashboard from "./jobs-dashboard.vue";
import viewEmailModal from "../modals/modal-viewemail";
import onboardingModelState from "../modals/modal-onboardingTodos.vue";
import { ONBOARDING_TODO_ITEMS, PROOF_ACCESS_PERMISSIONS } from "../../utils/constants";
import {
  getCurrentDateString,
  getTimeBasedGreeting,
  getPendingTodoItems,
  sortEventsChronologically,
} from "../../utils/helpers";

const { OWNED_ONLY, TEAMS_ONLY, NONE } = PROOF_ACCESS_PERMISSIONS;

const FILTERS = {
  proofId: 0,
  senderId: 0,
  workflowId: 0,
  approverId: 0,
  overdue: false,
  sortByDeadlineDesc: false,
  teamId: 0,
  text: "",
  status: 0,
};

const PERIOD_STORAGE_KEY = "overview::period";
const MODE_STORAGE_KEY = "overview::mode";
const ACCOUNT_STATE_CARDS = ["Proofs In Review", "Remaining Files", "Storage Usage"];

const ACCOUNT_STATE_TOOLTIPS = {
  "Proofs In Review":
    "Proofs that have been sent and are waiting on reviewer decisions across your whole account. Drafts and archived proofs aren't counted.",
};

// Plausible-looking numbers shown blurred until the user creates their first proof
const BLUR_PLACEHOLDERS = {
  "Total Proofs Created": { number: "24", subTitle: "In selected period" },
  "Total New Versions Sent": { number: "7", subTitle: "Versions after the first" },
  "Total Files Uploaded": { number: "112", subTitle: "In selected period" },
  "Avg Files Per Proof": { number: "4.6", subTitle: "Across your proofs" },
  "Avg Versions Per Proof": { number: "1.4", subTitle: "Including the first version" },
  "Avg Reminders Per Proof": { number: "2.3", subTitle: "Per approver" },
  "Avg Response Time": { number: "1.8", subTitle: "Days From Send to Decision, Per Approver" },
  "Approval Rates": { number: "86%", subTitle: "Create a proof to see your numbers" },
};

const PRESET_RANGES = {
  "7d": { amount: 7, unit: "days" },
  "30d": { amount: 30, unit: "days" },
  "90d": { amount: 90, unit: "days" },
  "12m": { amount: 12, unit: "months" },
};

export default {
  name: "overview-dashboard",
  components: {
    periodFilter,
    statCards,
    proofsChart,
    activityFeed,
    recentProofLists,
    gettingStarted,
    jobsDashboard,
    viewEmailModal,
    onboardingModelState,
  },
  data() {
    let preset = "12m";
    try {
      const stored = window.localStorage.getItem(PERIOD_STORAGE_KEY);
      if (stored && (PRESET_RANGES[stored] || stored === "all")) {
        preset = stored;
      }
    } catch (e) {
      // localStorage unavailable; keep default
    }
    let mode = "proofs";
    try {
      if (window.localStorage.getItem(MODE_STORAGE_KEY) === "jobs") {
        mode = "jobs";
      }
    } catch (e) {
      // localStorage unavailable; keep default
    }
    const sku = _.get(this.$A, "Auth.account.subscriptionState.sku", "");
    return {
      mode,
      isPremiumAccount: sku.includes("enterprise") || sku.includes("premium"),
      period: { preset, ...this.rangeForPreset(preset) },
      overviewMetrics: null,
      accountStateCards: [],
      timelineEvents: [],
      metricsLoading: true,
      activityLoading: true,
      loaded: false,
      proofAccess: null,
      curUserAssociatedProofs: [],
      onboardingState: null,
      isLoading: true,
      todoList: Object.values(ONBOARDING_TODO_ITEMS),
      pendingTodoItems: [],
      currentOnboardingStep: ONBOARDING_TODO_ITEMS.SHARE_REVIEW_LINK,
      showOnboardingStepModal: false,
    };
  },
  computed: {
    greeting() {
      const name = _.get(this.$A, "Auth.name", "");
      return `${getTimeBasedGreeting()}${name ? `, ${name}` : ""}`;
    },
    currentDate() {
      return getCurrentDateString();
    },
    hasCreatedProof() {
      return _.get(this, "onboardingState.todoState.hasCreatedProof", true);
    },
    blurStats() {
      return !this.hasCreatedProof;
    },
    filteredCards() {
      const cards = _.get(this, "overviewMetrics.reportCards", []) || [];
      if (!this.blurStats) {
        return cards;
      }
      return cards.map((card) => ({
        ...card,
        ...(BLUR_PLACEHOLDERS[card.description] || { number: "42" }),
        description: card.description,
      }));
    },
    chartData() {
      return _.get(this, "overviewMetrics.chart", { categories: [], series: [] });
    },
    hasPendingTodos() {
      return this.todoList.some((item) => !item.completed);
    },
    showJobs() {
      return this.mode === "jobs";
    },
  },
  async mounted() {
    try {
      this.onboardingState = await this.$A.UserService.getOnboardingState();
    } catch (error) {
      console.error("Failed to load onboarding state:", error);
    }

    await this.loadPermissionContext();
    await Promise.all([this.fetchFiltered(), this.fetchAccountStateCards()]);
    this.loaded = true;

    try {
      const { todoState, modelState } = this.onboardingState;
      const shouldShowReviewLinkModal =
        todoState.hasReviewLinkRegistered && !modelState.hasReviewLinkRegisteredModelShown;
      if (shouldShowReviewLinkModal) {
        this.pendingTodoItems = getPendingTodoItems(this.onboardingState);
        this.showOnboardingStepModal = true;
        await this.$A.UserService.updateOnboardingState({
          modelState: { hasReviewLinkRegisteredModelShown: true },
        });
      }
    } catch (loadError) {
      console.error("Failed to load onboarding modal:", loadError);
    }
  },
  methods: {
    setMode(mode) {
      this.mode = mode;
      try {
        window.localStorage.setItem(MODE_STORAGE_KEY, mode);
      } catch (e) {
        // localStorage unavailable; skip persisting
      }
    },
    rangeForPreset(preset) {
      const range = PRESET_RANGES[preset];
      if (!range) {
        return { startDate: null, endDate: null };
      }
      return {
        startDate: moment().endOf("day").subtract(range.amount, range.unit).startOf("day").toISOString(),
        endDate: moment().endOf("day").toISOString(),
      };
    },
    async onPeriodChange(period) {
      this.period = period;
      try {
        if (period.preset !== "custom") {
          window.localStorage.setItem(PERIOD_STORAGE_KEY, period.preset);
        }
      } catch (e) {
        // localStorage unavailable; skip persisting
      }
      await this.fetchFiltered();
    },
    async fetchFiltered() {
      this.metricsLoading = true;
      this.activityLoading = true;
      const [metrics, timeline] = await Promise.all([
        this.$A.AccountService.GetOverviewMetrics(this.period.startDate, this.period.endDate),
        this.$A.AccountService.GetTimelineEventsRange(this.period.startDate, this.period.endDate),
      ]);
      this.overviewMetrics = metrics || { reportCards: [], chart: { categories: [], series: [] } };
      this.timelineEvents = sortEventsChronologically(this.applyPermissionFilter(timeline || []));
      this.metricsLoading = false;
      this.activityLoading = false;
    },
    async fetchAccountStateCards() {
      const allCards = (await this.$A.AccountService.AccountStats()) || [];
      this.accountStateCards = allCards
        .filter((c) => ACCOUNT_STATE_CARDS.indexOf(c.description) !== -1)
        .map((c) => ({ ...c, tooltip: ACCOUNT_STATE_TOOLTIPS[c.description] }));
    },
    async loadPermissionContext() {
      const curUser = await this.$A.UserService.GetUser();
      this.proofAccess = _.get(curUser, "permissions.proofAccess");
      if (this.proofAccess === OWNED_ONLY) {
        this.curUserAssociatedProofs = (await this.getAssociatedProofs(curUser.id)) || [];
      } else if (this.proofAccess === TEAMS_ONLY) {
        this.curUserAssociatedProofs = (await this.getAssociatedProofs(curUser.id, curUser.teams)) || [];
      }
    },
    async getAssociatedProofs(curUserId = 0, teams = []) {
      if (_.isEmpty(teams)) {
        return (await this.$A.ProofService.GetProofsByFilter({ ...FILTERS, senderId: curUserId })) || [];
      }
      let teamsProofs = [];
      for (const each of teams) {
        const proofs = await this.$A.ProofService.GetProofsByFilter({ ...FILTERS, teamId: each.id });
        proofs.forEach((proof) => {
          if (!teamsProofs.some((p) => p.id === proof.id)) teamsProofs.push(proof);
        });
      }
      return teamsProofs;
    },
    applyPermissionFilter(events) {
      switch (this.proofAccess) {
        case OWNED_ONLY:
        case TEAMS_ONLY:
          return events.filter((event) =>
            this.curUserAssociatedProofs.some((proof) => proof.id === event.proofId)
          );
        case NONE:
          return [];
        default:
          return events;
      }
    },
    viewProof(proofId) {
      this.$A.NavigateToView(`/proof-item?id=${proofId}`);
    },
    viewApprover(approverId) {
      this.$A.NavigateToView(`/approver-item?id=${approverId}`);
    },
    viewEmail(emailLogId) {
      this.$refs.emailModal.viewEmail(emailLogId);
    },
    updateTodoList(todoState) {
      this.todoList = this.todoList.map((item) => {
        if (item.title === ONBOARDING_TODO_ITEMS.CREATE_CHECKLIST.title) {
          return item;
        }
        let completed = false;
        switch (item.title) {
          case ONBOARDING_TODO_ITEMS.CREATE_PROOF.title:
            completed = todoState.hasCreatedProof;
            break;
          case ONBOARDING_TODO_ITEMS.SHARE_REVIEW_LINK.title:
            completed = todoState.hasReviewLinkRegistered;
            break;
          case ONBOARDING_TODO_ITEMS.CREATE_EMAIL_TEMPLATE.title:
            completed = todoState.hasEmailTemplateCreated;
            break;
          case ONBOARDING_TODO_ITEMS.SEND_WORKFLOW.title:
            completed = todoState.hasUsedWorkflow;
            break;
        }
        return { ...item, completed };
      });
    },
  },
  watch: {
    onboardingState: {
      immediate: true,
      handler(newState) {
        if (newState && newState?.todoState) {
          this.updateTodoList(newState.todoState);
          this.isLoading = false;
        }
      },
    },
  },
};
</script>

<template>
  <div class="dashboard">
    <!-- Header row -->
    <div class="dashboard-head">
      <div class="dashboard-greeting">
        <div class="date-eyebrow">{{ currentDate }}</div>
        <h1 class="greeting-title">{{ greeting }}</h1>
      </div>
      <div class="dashboard-controls">
        <div class="mode-toggle" role="group" aria-label="Show metrics for">
          <button type="button" :class="{ active: !showJobs }" @click="setMode('proofs')">Proofs</button>
          <button type="button" :class="{ active: showJobs }" @click="setMode('jobs')">Jobs</button>
        </div>
        <period-filter :value="period" @input="onPeriodChange" />
      </div>
    </div>

    <!-- Jobs mode -->
    <jobs-dashboard v-if="showJobs" :period="period" :premium="isPremiumAccount" />

    <!-- Getting started (onboarding) -->
    <getting-started v-if="!showJobs && !isLoading && hasPendingTodos" :todo-list="todoList" />

    <!-- Chart + activity -->
    <div v-if="!showJobs" class="chart-activity-row">
      <proofs-chart :chart-data="chartData" :loading="metricsLoading" />
      <activity-feed
        :events="timelineEvents"
        :loading="activityLoading"
        :loaded="loaded"
        @view-email="viewEmail"
        @view-proof="viewProof"
        @view-approver="viewApprover"
      />
    </div>

    <!-- Current-state proof lists (not filtered by period) -->
    <recent-proof-lists v-if="!showJobs && loaded" :associated-proofs="curUserAssociatedProofs" />

    <!-- Filtered stat cards -->
    <stat-cards v-if="!showJobs" :cards="filteredCards" :loading="metricsLoading" :blurred="blurStats" />

    <!-- Unfiltered account-state cards -->
    <div v-if="!showJobs && accountStateCards.length" class="account-section">
      <span class="account-row-label">Account</span>
      <stat-cards :cards="accountStateCards" />
    </div>

    <!-- Modals -->
    <viewEmailModal ref="emailModal" />
    <onboardingModelState
      v-if="showOnboardingStepModal"
      :todos="pendingTodoItems"
      :current-onboarding-step="currentOnboardingStep"
    />
  </div>
</template>

<style scoped>
.dashboard {
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 1320px;
  margin: 0 auto;
  padding: 24px;
}
.dashboard-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.date-eyebrow {
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 6px;
}
.greeting-title {
  font-size: 28px;
  line-height: 34px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #111827;
  margin: 0;
}
.dashboard-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.mode-toggle {
  display: inline-flex;
  height: 38px;
  padding: 3px;
  gap: 2px;
  background: #e9ebef;
  border-radius: 8px;
}
.mode-toggle button {
  border: 0;
  background: transparent;
  color: #4b5563;
  font-size: 13px;
  font-weight: 600;
  padding: 0 14px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease;
}
.mode-toggle button:hover {
  color: #111827;
}
.mode-toggle button.active {
  background: #fff;
  color: #111827;
  box-shadow: 0 1px 2px rgba(8, 11, 13, 0.08);
}
.account-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.account-row-label {
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.chart-activity-row {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 16px;
  align-items: stretch;
}
.chart-activity-row > * {
  min-width: 0;
}
@media (max-width: 1118px) {
  .chart-activity-row {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 575px) {
  .dashboard {
    padding: 16px;
    gap: 16px;
  }
  .greeting-title {
    font-size: 22px;
    line-height: 28px;
  }
}
</style>
